import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { Search, ShieldCheck, ShieldAlert, Shield, Image as ImageIcon, CheckCircle, XCircle, UserX, Clock, ExternalLink, RefreshCw } from 'lucide-react';

const Verifications = () => {
  const { colors } = useTheme();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [proofVerification, setProofVerification] = useState(null);
  const [proofUrls, setProofUrls] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, pending: 0, verified: 0, rejected: 0 });

  useEffect(() => {
    fetchVerifications();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchStats = async () => {
    try {
      const verificationSnapshot = await getDocs(collection(db, 'identity_verifications'));
      const statsData = { total: 0, pending: 0, verified: 0, rejected: 0 };
      
      verificationSnapshot.docs.forEach(doc => {
        const data = doc.data();
        statsData.total++;
        switch (data.status) {
          case 'pending':
            statsData.pending++;
            break;
          case 'verified':
            statsData.verified++;
            break;
          case 'rejected':
            statsData.rejected++;
            break;
          default:
            break;
        }
      });
      
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      let q;
      if (filter === 'all') {
        q = query(collection(db, 'identity_verifications'), orderBy('createdAt', 'desc'));
      } else {
        q = query(
          collection(db, 'identity_verifications'),
          where('status', '==', filter),
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      
      const verificationsWithUsers = await Promise.all(
        snapshot.docs.map(async (verificationDoc) => {
          const verificationData = verificationDoc.data();
          const userId = verificationData.userId;
          
          const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
          const userData = userDoc.docs[0]?.data() || {};
          
          return {
            id: verificationDoc.id,
            verificationId: verificationDoc.id,
            userId: userId,
            ...verificationData,
            ...userData,
            createdAt: verificationData.createdAt?.toDate?.() || new Date(),
            status: verificationData.status || 'pending',
          };
        })
      );
      
      setVerifications(verificationsWithUsers);
    } catch (error) {
      console.error('Error fetching verifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (verification) => {
    try {
      await updateDoc(doc(db, 'identity_verifications', verification.verificationId), {
        status: 'verified',
        reviewedAt: Timestamp.now(),
        reviewedBy: 'admin',
      });
      
      await updateDoc(doc(db, 'users', verification.userId), {
        verificationStatus: 'verified',
        role: 'host',
        verifiedAt: Timestamp.now(),
        verifiedBy: 'admin',
        updatedAt: Timestamp.now(),
      });
      
      fetchVerifications();
      fetchStats();
    } catch (error) {
      console.error('Error approving verification:', error);
      alert('Error approving verification: ' + error.message);
    }
  };

  const handleReject = async () => {
    if (!selectedVerification || !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      const isRevoking = selectedVerification.status === 'verified';
      
      await updateDoc(doc(db, 'identity_verifications', selectedVerification.verificationId), {
        status: 'rejected',
        rejectionReason: rejectionReason,
        reviewedAt: Timestamp.now(),
        reviewedBy: 'admin',
        ...(isRevoking && { revokedAt: Timestamp.now() }),
      });
      
      const userUpdate = {
        verificationStatus: 'rejected',
        rejectionReason: rejectionReason,
        updatedAt: Timestamp.now(),
      };
      
      if (isRevoking) {
        userUpdate.role = 'guest';
        userUpdate.revokedAt = Timestamp.now();
        userUpdate.verifiedAt = null;
        userUpdate.verifiedBy = null;
      }
      
      await updateDoc(doc(db, 'users', selectedVerification.userId), userUpdate);
      
      setShowRejectModal(false);
      setSelectedVerification(null);
      setRejectionReason('');
      fetchVerifications();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting verification:', error);
      alert('Error rejecting verification: ' + error.message);
    }
  };

  const handleOpenProofModal = async (verification) => {
    setProofVerification(verification);
    setShowProofModal(true);
    setProofUrls(null);
    setProofLoading(true);

    try {
      const getVerificationDocumentAccess = httpsCallable(functions, 'getVerificationDocumentAccess');
      const result = await getVerificationDocumentAccess({
        verificationId: verification.verificationId,
      });
      setProofUrls(result.data || null);
    } catch (error) {
      console.error('Error fetching secure document URLs:', error);
      setProofUrls(null);
      alert('Could not load secure document links.');
    } finally {
      setProofLoading(false);
    }
  };

  const resolveProofUrl = (secureUrl, fallbackUrl) => {
    if (secureUrl) {
      return secureUrl;
    }
    if (!fallbackUrl || fallbackUrl === 'pending_upload') {
      return null;
    }
    if (/^https?:\/\//i.test(fallbackUrl)) {
      return fallbackUrl;
    }
    return null;
  };

  // Document types that require a back image (matches the Flutter app's
  // IdentityVerificationScreen._needsBackImage rule).
  const docTypeRequiresBack = (idType) =>
    idType === 'nationalId' || idType === 'drivingLicense';

  // Determine which documents are expected for a verification based on its ID type,
  // and validate that the type and all required images are present.
  const getDocumentChecklist = (verification) => {
    const idType = verification?.idType;
    const needsBack = docTypeRequiresBack(idType);

    const items = [
      {
        key: 'front',
        label: 'ID Document (Front)',
        required: true,
        url: resolveProofUrl(proofUrls?.idImageUrl, verification?.idImageUrl),
      },
    ];

    if (needsBack) {
      items.push({
        key: 'back',
        label: 'ID Document (Back)',
        required: true,
        url: resolveProofUrl(proofUrls?.idBackImageUrl, verification?.idBackImageUrl),
      });
    }

    items.push({
      key: 'selfie',
      label: 'Selfie Verification',
      required: true,
      url: resolveProofUrl(proofUrls?.selfieUrl, verification?.selfieUrl),
    });

    const hasIdType = !!idType;
    const missing = items.filter((i) => i.required && !i.url).map((i) => i.label);
    const isComplete = hasIdType && missing.length === 0;

    return { items, hasIdType, needsBack, missing, isComplete };
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, icon: Clock, text: 'Pending' },
      verified: { bg: colors.successGhost, color: colors.success, icon: ShieldCheck, text: 'Verified' },
      rejected: { bg: colors.errorGhost, color: colors.error, icon: XCircle, text: 'Rejected' },
      notStarted: { bg: colors.primaryGhost, color: colors.textMuted, icon: ShieldAlert, text: 'Not Started' }
    };

    const badge = badges[status] || badges.notStarted;
    const Icon = badge.icon;

    return (
      <div 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase shadow-sm"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30` }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </div>
    );
  };

  const filteredVerifications = verifications.filter(v => 
    (v.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (v.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const kpis = [
    { id: 'pending', label: 'Pending Review', value: stats.pending, icon: ShieldAlert, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'verified', label: 'Verified Hosts', value: stats.verified, icon: ShieldCheck, color: colors.success, gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' },
    { id: 'rejected', label: 'Rejected', value: stats.rejected, icon: XCircle, color: colors.error, gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' },
    { id: 'all', label: 'Total Submissions', value: stats.total, icon: Shield, color: colors.textSecondary, gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Host Verifications
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Review, approve, and manage identity verification requests.
          </p>
        </div>
        <button 
          onClick={() => { fetchVerifications(); fetchStats(); }}
          className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors flex items-center gap-2"
          style={{ backgroundColor: colors.surfaceHover, color: colors.text, border: `1px solid ${colors.border}` }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh Data
        </button>
      </div>

      {/* KPI Cards / Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => {
          const isActive = filter === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i+1}`}
              style={{
                backgroundColor: isActive ? `${kpi.color}10` : colors.card,
                border: `1px solid ${isActive ? kpi.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${kpi.color}20` : undefined,
                ringColor: isActive ? kpi.color : 'transparent'
              }}
              onClick={() => setFilter(kpi.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-wider mb-1" style={{ color: isActive ? kpi.color : colors.textSecondary }}>{kpi.label}</p>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{kpi.value}</p>
                </div>
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ background: kpi.gradient }}
                >
                  <kpi.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

       {/* Search Bar */}
       <div className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="relative">
           <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]" style={{ color: colors.textMuted }} />
           <input
             type="text"
             placeholder="Search users by name or email..."
             className="w-full pl-11 pr-4 py-2.5 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
             style={{
               backgroundColor: colors.backgroundSecondary,
               border: `1px solid ${colors.border}`,
               color: colors.text
             }}
             onFocus={(e) => {
               e.target.style.borderColor = colors.borderActive;
               e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`;
             }}
             onBlur={(e) => {
               e.target.style.borderColor = colors.border;
               e.target.style.boxShadow = 'none';
             }}
             value={searchTerm}
             onChange={(e) => setSearchTerm(e.target.value)}
           />
        </div>
      </div>

      {/* Verifications Table */}
      <div className="card-premium overflow-hidden" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
             <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
             <p className="mt-4 text-sm font-medium" style={{ color: colors.textSecondary }}>Refreshing submissions...</p>
          </div>
        ) : filteredVerifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.backgroundSecondary }}>
               <Shield className="w-8 h-8" style={{ color: colors.textMuted }} />
            </div>
            <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>No {filter !== 'all' ? filter : ''} verifications found</h3>
            <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
               Try clearing filters or search terms.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>User Info</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>ID Type</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Date Submitted</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVerifications.map((user, index) => (
                  <tr 
                    key={user.id} 
                    className="group transition-colors"
                    style={{ borderBottom: index < filteredVerifications.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: colors.surface }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {user.profileImageUrl ? (
                          <img className="h-10 w-10 text-[10px] rounded-full object-cover border-2" style={{ borderColor: colors.border }} src={user.profileImageUrl} alt="Avatar" />
                        ) : (
                          <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)' }}>
                            {user.displayName?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                        <div>
                          <div className="text-[14px] font-bold" style={{ color: colors.text }}>{user.displayName || 'No Name'}</div>
                          <div className="text-[12px] font-medium" style={{ color: colors.textSecondary }}>{user.email || 'No Email'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <span className="text-[13px] font-medium px-2 py-1 rounded-md" style={{ backgroundColor: colors.backgroundSecondary, color: colors.text }}>
                         {user.idType ? user.idType.charAt(0).toUpperCase() + user.idType.slice(1) : 'Unknown ID'}
                       </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[13px] font-medium" style={{ color: colors.textSecondary }}>
                       {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       {getStatusBadge(user.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                       <button
                         onClick={() => { handleOpenProofModal(user); }}
                         className="px-4 py-2 rounded-xl text-[12px] font-bold transition-all inline-flex items-center gap-2 hover:shadow-md"
                         style={{ backgroundColor: colors.primary, color: '#fff' }}
                       >
                         <CheckCircle className="w-3.5 h-3.5" /> Review Application
                       </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Proof Viewing Modal */}
      {showProofModal && proofVerification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
            <div className="rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative animate-fadeInScale flex flex-col" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="p-6 sm:p-8">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  {proofVerification.profileImageUrl ? (
                     <img className="h-14 w-14 rounded-full object-cover shadow-md" src={proofVerification.profileImageUrl} alt="Avatar" />
                  ) : (
                     <div className="h-14 w-14 rounded-full flex items-center justify-center font-bold text-white shadow-md text-xl" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)' }}>
                       {proofVerification.displayName?.charAt(0)?.toUpperCase() || '?'}
                     </div>
                  )}
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl font-bold" style={{ color: colors.text }}>
                        {proofVerification.displayName}
                      </h3>
                      {getStatusBadge(proofVerification.status)}
                    </div>
                    <p className="mt-0.5 text-[14px]" style={{ color: colors.textSecondary }}>
                      {proofVerification.email} • ID Type: {proofVerification.idType ? proofVerification.idType.charAt(0).toUpperCase() + proofVerification.idType.slice(1) : 'Unknown ID'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowProofModal(false); setProofVerification(null); }}
                  className="p-2.5 rounded-full backdrop-blur-md transition-all hover:bg-black/5"
                  style={{ backgroundColor: colors.backgroundSecondary, color: colors.text }}
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="my-6 border-t" style={{ borderColor: colors.border }} />

              {proofLoading && (
                <div className="mb-5 rounded-xl p-3 text-[13px] font-medium" style={{ backgroundColor: colors.primaryGhost, color: colors.primary }}>
                  Loading secure document links...
                </div>
              )}

              {(() => {
                const checklist = getDocumentChecklist(proofVerification);
                return (
                  <>
                    {/* Document completeness banner */}
                    {!proofLoading && (
                      checklist.isComplete ? (
                        <div className="mb-5 rounded-xl p-3 text-[13px] font-medium flex items-center gap-2" style={{ backgroundColor: colors.successGhost, color: colors.success, border: `1px solid ${colors.success}30` }}>
                          <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                          All required documents for a{' '}
                          {proofVerification.idType ? proofVerification.idType.charAt(0).toUpperCase() + proofVerification.idType.slice(1) : 'document'}{' '}
                          are present ({checklist.items.length} image{checklist.items.length > 1 ? 's' : ''}).
                        </div>
                      ) : (
                        <div className="mb-5 rounded-xl p-3 text-[13px] font-medium flex items-start gap-2" style={{ backgroundColor: colors.errorGhost, color: colors.error, border: `1px solid ${colors.error}30` }}>
                          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            {!checklist.hasIdType ? (
                              <span>Document type is missing — cannot validate this submission.</span>
                            ) : (
                              <span>Incomplete submission. Missing: {checklist.missing.join(', ')}.</span>
                            )}
                          </div>
                        </div>
                      )
                    )}

                    <div className={`grid grid-cols-1 md:grid-cols-2 ${checklist.items.length >= 3 ? 'lg:grid-cols-3' : ''} gap-6`}>
                      {checklist.items.map((item) => (
                        <div key={item.key} className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-[13px] font-semibold uppercase tracking-wider" style={{ color: colors.text }}>{item.label}</h4>
                            {item.required && !item.url && (
                              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: colors.errorGhost, color: colors.error }}>Missing</span>
                            )}
                          </div>
                          <div className="border rounded-xl overflow-hidden shadow-sm relative group aspect-[4/3] flex items-center justify-center" style={{ borderColor: item.required && !item.url ? colors.error : colors.border, backgroundColor: colors.backgroundSecondary }}>
                            {item.url ? (
                              <>
                                <img
                                  src={item.url}
                                  alt={item.label}
                                  className="w-full h-full object-contain"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                                <div className="hidden absolute inset-0 flex-col items-center justify-center text-center p-4">
                                   <ShieldAlert className="w-8 h-8 mb-2" style={{ color: colors.warning }} />
                                   <p className="text-xs font-medium" style={{ color: colors.text }}>Failed to load image</p>
                                </div>
                                <button onClick={() => window.open(item.url, '_blank')} className="absolute top-2 right-2 p-2 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-md hover:bg-black/70">
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <div className="text-center p-4">
                                 <ImageIcon className="w-8 h-8 mb-2 mx-auto" style={{ color: colors.textMuted }} />
                                 <p className="text-xs font-medium" style={{ color: colors.textSecondary }}>{item.required ? 'Missing — Required' : 'Not Provided'}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}

              {/* Action Buttons Header overlay or bottom stick */}
              <div className="sticky bottom-0 -mx-6 sm:-mx-8 p-6 sm:p-8 mt-8 rounded-b-2xl border-t bg-opacity-95 backdrop-blur-sm shadow-[0_-8px_30px_rgba(0,0,0,0.05)]" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
                <div className="flex flex-col sm:flex-row gap-3">
                  {proofVerification.status === 'pending' && (() => {
                     const canApprove = getDocumentChecklist(proofVerification).isComplete;
                     return (
                     <>
                        <button
                          onClick={() => { setShowProofModal(false); handleApprove(proofVerification); }}
                          disabled={!canApprove}
                          title={canApprove ? '' : 'Document type or required images are missing'}
                          className="flex-1 py-3.5 rounded-xl font-bold text-white shadow-md hover:shadow-lg flex justify-center items-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:shadow-none"
                          style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                        >
                          <ShieldCheck className="w-[18px] h-[18px]" /> Approve Host
                        </button>
                        <button
                          onClick={() => { setShowProofModal(false); setSelectedVerification(proofVerification); setShowRejectModal(true); }}
                          className="flex-1 py-3.5 rounded-xl font-bold text-white shadow-md hover:shadow-lg flex justify-center items-center gap-2 transition-transform hover:-translate-y-0.5"
                          style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                        >
                          <XCircle className="w-[18px] h-[18px]" /> Reject Application
                        </button>
                     </>
                     );
                  })()}
                  {proofVerification.status === 'verified' && (
                     <button
                        onClick={() => { setShowProofModal(false); setSelectedVerification(proofVerification); setShowRejectModal(true); }}
                        className="flex-1 py-3.5 rounded-xl font-bold transition-all flex justify-center items-center gap-2"
                        style={{ backgroundColor: colors.errorGhost, color: colors.error, border: `1px solid ${colors.error}40` }}
                      >
                        <UserX className="w-[18px] h-[18px]" /> Revoke Verification & Demote to Guest
                      </button>
                  )}
                  {proofVerification.status === 'rejected' && (() => {
                     const canApprove = getDocumentChecklist(proofVerification).isComplete;
                     return (
                     <button
                        onClick={() => { setShowProofModal(false); handleApprove(proofVerification); }}
                        disabled={!canApprove}
                        title={canApprove ? '' : 'Document type or required images are missing'}
                        className="flex-1 py-3.5 rounded-xl font-bold transition-all flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: colors.primaryGhost, color: colors.primary, border: `1px solid ${colors.primary}` }}
                      >
                        <CheckCircle className="w-[18px] h-[18px]" /> Re-Approve Host
                      </button>
                     );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.8)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl max-w-md w-full p-6 sm:p-8 animate-fadeInScale shadow-2xl" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.errorGhost }}>
               <XCircle className="w-6 h-6" style={{ color: colors.error }} />
            </div>
            <h3 className="text-xl font-bold" style={{ color: colors.text }}>
              {selectedVerification?.status === 'verified' ? 'Revoke Verification' : 'Reject Verification'}
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: colors.textSecondary }}>
              Please provide a reason for {selectedVerification?.status === 'verified' ? 'revoking' : 'rejecting'} {selectedVerification?.displayName}'s {selectedVerification?.status === 'verified' ? 'verification. This will demote them back to guest status.' : 'request.'}
            </p>

            <textarea
              className="w-full mt-5 p-3.5 rounded-xl text-[14px] focus:outline-none transition-all"
              style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
              rows={4}
              placeholder="e.g. ID is blurry, Selfie does not match ID..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.error; e.target.style.boxShadow = `0 0 0 3px ${colors.errorGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => { setShowRejectModal(false); setSelectedVerification(null); setRejectionReason(''); }}
                className="flex-[1] py-2.5 rounded-xl font-semibold transition-all hover:bg-opacity-80"
                style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="flex-[2] py-2.5 rounded-xl font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)' }}
              >
                 {selectedVerification?.status === 'verified' ? <UserX className="w-4 h-4"/> : <XCircle className="w-4 h-4" />}
                 {selectedVerification?.status === 'verified' ? 'Revoke' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Verifications;